/**
 * Every word the onboarding says, in one file.
 *
 * THE COPY RULE, and it is the whole point of the feature: a thirteen-year-old
 * reads this and understands it. That is not a vague aspiration, it is four
 * things a reviewer can check:
 *
 *   1. **Short sentences.** Under about sixteen words. One idea each.
 *   2. **Common words.** "Message", not "inquiry record". "Person", not
 *      "entity". Where the product's own word is on the screen, the copy says
 *      the plain word first and the product's word second, in that order —
 *      "the deal's stage (which step it is at)".
 *   3. **No undefined jargon.** Every CRM noun the product prints is in
 *      `GLOSSARY` below, and no line of copy uses one of those nouns before
 *      the glossary has defined it. The screen puts the glossary on the page,
 *      not behind a link, for exactly that reason.
 *   4. **Say what happens, not what a thing is.** "Click a row and the panel
 *      on the right fills up" beats "the detail pane is bound to selection".
 *
 * The copy lives here rather than inside the components because it is the part
 * a non-engineer edits, and because the checklist, the tour and the glossary
 * have to agree with each other about what a word means. One file, one
 * vocabulary.
 *
 * Nothing here renders. It only describes.
 */

import { CheckSquare, Inbox, Kanban, MousePointerClick, Star, UserCheck } from 'lucide-react';

import { ROUTES, type IconComponent } from '@/lib/constants';

/* ────────────────────────────────────────────────────────────────────────────
   The glossary

   Fifteen words the product prints that nobody outside a sales team has ever
   needed. Each one gets a `plain` line that could be said out loud, and an
   optional `more` line for the part that only matters once the plain line has
   landed. `more` is never required to use the app.
   ──────────────────────────────────────────────────────────────────────────── */

export interface GlossaryTerm {
  /** The word as the product prints it. */
  term: string;
  /** One sentence. Plain words only. This is the definition that counts. */
  plain: string;
  /** The second sentence, for the part that is useful but not needed. */
  more?: string;
}

/**
 * Ordered so that reading top to bottom builds up: the things first, then what
 * happens to them, then the numbers about them. A term never leans on a term
 * below it.
 */
export const GLOSSARY: GlossaryTerm[] = [
  {
    term: 'Inquiry',
    plain: 'A message from someone who might want to buy something from us.',
    more: 'Most arrive from a form on the website. Every one gets a short code like AF-2418 so you can talk about it.',
  },
  {
    term: 'Contact',
    plain: 'A person. Their name, their email, their phone number.',
  },
  {
    term: 'Company',
    plain: 'The business a person works for.',
    more: 'One company usually has several contacts, and several inquiries.',
  },
  {
    term: 'Pipeline',
    plain: 'The path an inquiry walks, from "just arrived" to "we won it" or "we lost it".',
  },
  {
    term: 'Stage',
    plain: 'Which step of that path an inquiry is standing on right now.',
    more: 'New, Triage, Qualifying, Review, Proposal, then Won or Lost. Moving an inquiry forward means changing its stage.',
  },
  {
    term: 'Owner',
    plain: 'The one person looking after an inquiry.',
    more: 'If nobody owns it, everybody assumes somebody else is on it. That is how inquiries go cold.',
  },
  {
    term: 'Task',
    plain: 'A note to yourself that says "do this next", with a date on it.',
  },
  {
    term: 'Note',
    plain: 'Something you write down for your team. The customer never sees it.',
  },
  {
    term: 'Reply',
    plain: 'An email you send back to the customer. They do see it.',
  },
  {
    term: 'Tag',
    plain: 'A sticky label you put on an inquiry so you can find it again later.',
  },
  {
    term: 'Saved view',
    plain: 'A search you liked enough to keep. It gets its own name in the left rail.',
    more: 'Filter a list down to what you care about, press Save, and it is one click away forever.',
  },
  {
    term: 'Filter',
    plain: 'A rule that hides the rows you did not ask for.',
  },
  {
    term: 'Score',
    plain: 'A number out of 100 guessing how real an inquiry is.',
    more: 'High means funded, scoped and moving. It is a hint, not a verdict — you can disagree with it.',
  },
  {
    term: 'SLA',
    plain: 'A promise about how fast we answer. The clock shows how much of it is left.',
    more: 'Short for "service level agreement". When the clock runs out the inquiry turns red, which is the point of it.',
  },
  {
    term: 'Board',
    plain: 'The same inquiries as the Inbox, drawn as columns instead of rows.',
    more: 'One column per stage. Drag a card sideways and you have moved that inquiry along.',
  },
  {
    term: 'Archive',
    plain: 'Put an inquiry away without deleting it. Nothing is ever really deleted here.',
  },
];

/* ────────────────────────────────────────────────────────────────────────────
   The tour

   Seven stops over the app's own chrome. `anchor` is a CSS selector for a real
   element — the tour highlights the thing it is talking about rather than
   drawing a picture of it — and a stop whose anchor is not on screen (the rail
   is a drawer on a phone; the record pane is not open yet) still renders, just
   centred and without the spotlight. A tour that breaks when the layout changes
   is a tour that teaches the wrong layout.
   ──────────────────────────────────────────────────────────────────────────── */

export interface TourStop {
  id: string;
  /** Selector for the element to spotlight, or `null` for a centred card. */
  anchor: string | null;
  title: string;
  /** Two or three short sentences. Same copy rule as everything else here. */
  body: string;
}

export const TOUR: TourStop[] = [
  {
    id: 'welcome',
    anchor: null,
    title: 'This is where the messages land',
    body: 'People fill in a form on the AlphaForm website. What they wrote arrives here, in this app. Your job is to answer them and keep track of what happens next. Six more screens and you have seen all of it.',
  },
  {
    id: 'nav',
    anchor: '[data-tour="nav"]',
    title: 'The left rail is the whole app',
    body: 'Inbox is every message. Board is the same messages drawn as columns. Contacts and Companies are the people and the businesses. Tasks is your own to-do list.',
  },
  {
    id: 'main',
    anchor: '[data-tour="main"]',
    title: 'The big area is the work',
    body: 'Whatever you picked in the rail is drawn here. In the Inbox it is a table, one row per message. Click a row and a panel opens on the right with everything about that person.',
  },
  {
    id: 'search',
    anchor: '[data-tour="search"]',
    title: 'Search finds anything',
    body: 'Type a name, a company, or a code like AF-2418. It searches messages, people and businesses at the same time. The keyboard shortcut is Ctrl+K, or Cmd+K on a Mac.',
  },
  {
    id: 'create',
    anchor: '[data-tour="create"]',
    title: 'New makes things',
    body: 'A message that came in by phone, a person who is not in here yet, a task for later. If you need to add something, it starts at this button.',
  },
  {
    id: 'account',
    anchor: '[data-tour="account"]',
    title: 'That circle is you',
    body: 'Your name, your settings, and the way out. The list of keyboard shortcuts is in there too — or press the question mark key at any time.',
  },
  {
    id: 'end',
    anchor: null,
    title: 'That is the tour',
    body: 'Nothing you press in here can break anything. Every change is written down and can be undone. The checklist on this page gives you six small things to try — do them in any order, or ignore them.',
  },
];

/* ────────────────────────────────────────────────────────────────────────────
   The checklist

   Six steps. Each one is a real thing to do in the product, and each one is
   ticked by EVIDENCE rather than by the reader saying they did it — see
   `useOnboarding`. Two kinds of evidence:

     `visit`  you went to that screen. Recorded by the shell as you navigate.
     `data`   the workspace can prove it: you own an inquiry, a task is yours,
              a view has your name on it.

   The `data` steps can be true the moment you first sign in, because a
   workspace that has been running for a year already has your name on things.
   That is not a bug and the card says so out loud ("already true — you own 12")
   rather than pretending you just did it. The teaching is in the card, not in
   the tick.
   ──────────────────────────────────────────────────────────────────────────── */

export type StepId = 'inbox' | 'record' | 'board' | 'owner' | 'task' | 'view';

export interface StepMeta {
  id: StepId;
  icon: IconComponent;
  /** An instruction, in the imperative. Under eight words. */
  title: string;
  /** What the thing is. One sentence, plain words. */
  what: string;
  /** How to do it, as if over your shoulder. */
  how: string;
  /** Why anyone should care. This is the sentence people actually remember. */
  why: string;
  /** Where the button on the card goes. */
  to: string;
  /** The button's word. */
  cta: string;
  kind: 'visit' | 'data';
}

export const STEPS: StepMeta[] = [
  {
    id: 'inbox',
    icon: Inbox as IconComponent,
    title: 'Open the Inbox',
    what: 'The Inbox is every message anyone has sent us, newest first.',
    how: 'Click Inbox in the left rail.',
    why: 'This is the screen you will live on. Everything else is a different view of it.',
    to: ROUTES.inbox,
    cta: 'Go to the Inbox',
    kind: 'visit',
  },
  {
    id: 'record',
    icon: MousePointerClick as IconComponent,
    title: 'Open one message',
    what: 'Clicking a row opens the full record: who sent it, what they asked for, what we have said back.',
    how: 'In the Inbox, click any row. Press Escape to close it again.',
    why: 'Nothing you can click in there is dangerous. Have a look around before you touch anything.',
    to: ROUTES.inbox,
    cta: 'Open a message',
    kind: 'visit',
  },
  {
    id: 'board',
    icon: Kanban as IconComponent,
    title: 'Look at the Board',
    what: 'The Board is the same messages as the Inbox, drawn as columns instead of rows.',
    how: 'Click Board in the left rail. Each column is one step of the pipeline.',
    why: 'A table tells you what arrived. The Board tells you where everything is stuck.',
    to: ROUTES.board,
    cta: 'Go to the Board',
    kind: 'visit',
  },
  {
    id: 'owner',
    icon: UserCheck as IconComponent,
    title: 'Put your name on one',
    what: 'The owner is the one person looking after a message.',
    how: 'Open a message, find Owner on the right, and pick yourself.',
    why: 'With no owner, everyone assumes someone else replied. Nobody did.',
    to: ROUTES.inbox,
    cta: 'Pick a message to own',
    kind: 'data',
  },
  {
    id: 'task',
    icon: CheckSquare as IconComponent,
    title: 'Make yourself a task',
    what: 'A task is a note that says "do this next", with a date attached.',
    how: 'Open a message, scroll to Tasks, write one line and give it a date.',
    why: 'A promise you made in an email is not written down anywhere. A task is.',
    to: ROUTES.tasks,
    cta: 'Go to Tasks',
    kind: 'data',
  },
  {
    id: 'view',
    icon: Star as IconComponent,
    title: 'Save a search',
    what: 'A saved view is a search you liked enough to keep. It gets a name in the left rail.',
    how: 'In the Inbox, narrow the list with the filter button, then press Save view.',
    why: 'The list you want on a Monday morning is one click away instead of six.',
    to: ROUTES.inbox,
    cta: 'Go and filter a list',
    kind: 'data',
  },
];

/** Same order as `STEPS`, so a caller can index either. */
export const STEP_IDS: StepId[] = STEPS.map((step) => step.id);
